import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import DepartmentImage from '../../../assets/image/department/dmv-office-exterior.jpg';
import DepartmentImage2 from '../../../assets/image/department/driving-test.jpg';

function DMVDetails() {
  const [Faq, setFaq] = useState(0);

  // theme colors from DOM
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');
  const [bodyColor, setBodyColor] = useState('');

  useEffect(() => {
    if (titleRef.current) setTitleColor(window.getComputedStyle(titleRef.current).color);
    if (bodyRef.current) setBodyColor(window.getComputedStyle(bodyRef.current).color);
  }, []);

  const overviewEmphasis = { color: titleColor || '#0f172a' };

  // compact stacked subtitle/description layout
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35, color: titleColor || '#0f172a' };
  const valText = { fontSize: '0.95rem', lineHeight: 1.6, color: bodyColor || undefined };

  const linkify = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) =>
      /^https?:\/\/[^\s]+$/.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
  };

  const Drop = [
    {
      Que: "How do I renew my driver's license?",
      Answer:
        "You can renew your driver's license online, by mail, or in person at a DMV office. Check eligibility and required documents before renewing. For more details please see: https://state-of-gts.web.app/department/dmv/license-renewal",
    },
    {
      Que: 'What documents are required to register a vehicle?',
      Answer:
        "To register a vehicle, you'll need proof of ownership (title), proof of insurance, an emissions test (if applicable), and a completed application form.",
    },
    {
      Que: 'How can I schedule a driving test?',
      Answer:
        "Driving tests can be scheduled online through the DMV website or by calling your local DMV office. Availability may vary based on location.",
    },
  ];

  return (
    <>
      <section className="department-details-section">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-12 col-lg-4 col-xl-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="department-details-imgbox">
                <img src={DepartmentImage} className="img-fluid" alt="DMV Office" />
              </div>

              <div className="department-details-content-box">
                <h4 ref={titleRef} className="department-details-title">Motor Vehicles (DMV)</h4>
                <p style={overviewEmphasis}>
                  We deliver licensing, identification, and vehicle services with a focus on safety, accuracy, and speed.
                  Priorities: shorter wait times, clear requirements, and secure credentials.
                </p>
                <p>
                  The DMV provides driver licensing and ID cards, vehicle titling and registration, testing, and records.
                  We streamline processes through online transactions, scheduled appointments, and self-service tools—while
                  maintaining strong identity verification and fraud prevention.
                </p>
              </div>

              <div className="department-details-box">
                <div className="department-details-policy">
                  <span>Dedicated to efficient, customer-friendly service for all drivers and vehicle owners.</span>
                </div>
                <p ref={bodyRef}>
                  Whether you're applying for a new license, registering your vehicle, or ensuring compliance with driving laws,
                  our services are designed to make your experience smooth and convenient. We partner with law enforcement,
                  courts, inspection stations, and other agencies to keep roads safe and records accurate.
                </p>
              </div>

              <div className="department-details-benefits-inner-box">
                <div className="row row-gutter-30">
                  <div className="col-lg-6">
                    <div className="department-details-benefits-box-image">
                      <img src={DepartmentImage2} className="img-fluid" alt="Driving Test" />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="department-details-benefits-box">
                      <h3>Our Services</h3>
                      <p>Comprehensive motor vehicle and licensing services for residents.</p>
                      <ul className="list-unstyled list-style">
                        <li>
                          <Link to="/department/dmv/license-renewal">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Driver’s License Renewal & Issuance
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/dmv/vehicle-registration">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Vehicle Registration & Title Transfers
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/dmv/driving-tests">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Driving Tests & Learner’s Permits
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/dmv/id-cards">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            State Identification Cards & Real ID
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Outcomes section */}
              <div className="department-details-content-box" style={{ marginTop: '10px' }}>
                <h4
                  className="department-details-title"
                  style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}
                >
                  Outcomes we aim for
                </h4>
                <p style={{ color: bodyColor || undefined, fontSize: '0.95rem' }}>
                  Shorter in-office visits, higher completion rates for online services, fewer retests caused by paperwork
                  errors, more on-time renewals, increased REAL ID uptake during renewal, and improved testing throughput
                  with better prep resources and clear checklists.
                </p>
              </div>

              {/* What We Do — compact stacked layout */}
              <div className="department-details-benefits-box">
                <h3>What We Do</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Licensing & Identification</div>
                      <div style={valText}>
                        New, renewal, and replacement licenses/IDs; address and name changes; REAL ID upgrades; medical and
                        vision compliance where applicable.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Vehicle Titles, Registration & Plates</div>
                      <div style={valText}>
                        Title transfers, new registrations, renewals, specialty plates, insurance verification, and emissions
                        or safety compliance when required.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Knowledge & Road Testing</div>
                      <div style={valText}>
                        Written exams, road tests, CDL/endorsements, study guides, and test scheduling with clear eligibility
                        rules and preparation tips.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Digital Services & Appointments</div>
                      <div style={valText}>
                        Online renewals, duplicates, fee payments, record requests, and appointment booking to reduce lines
                        and streamline visits.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Customer Support, Accessibility & Compliance</div>
                      <div style={valText}>
                        Multilingual help, ADA accommodations, fraud prevention, records corrections, and guidance on
                        citations/holds affecting licensing or registration.
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="accordian-box">
                {Drop.map((items, index) => (
                  <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                    <div className="accordian-title">
                      <h5>{items.Que}</h5>
                      <i className="fa-solid fa-angle-right" onClick={() => setFaq(index)} />
                    </div>

                    {Faq === index && (
                      <div className="accordian-content" style={{ display: 'block' }}>
                        <p>{linkify(items.Answer)}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default DMVDetails;
